import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Card, 
  CardBody, 
  Button, 
  OutlineButton, 
  FlexContainer,
  Grid
} from '../../styles/StyledComponents';
import { 
  FaFilePdf, 
  FaFileExcel, 
  FaDownload, 
  FaChartBar,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaReceipt,
  FaExclamationTriangle
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

const PageContainer = styled.div`
  margin-bottom: 2rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .title-icon {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(246, 139, 33, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ProjectInfoCard = styled(Card)`
  margin-bottom: 2rem;
  border: 2px solid #F68B21;
  background: linear-gradient(135deg, #fff9f0 0%, #ffffff 100%);
  
  .project-header {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    color: white;
    padding: 1rem;
    border-radius: 8px 8px 0 0;
    
    h3 {
      margin: 0;
      font-size: 1.2rem;
      font-weight: 600;
    }
    
    .project-meta {
      margin-top: 0.5rem;
      font-size: 0.85rem;
      opacity: 0.9;
    }
  }
`;

const ReportsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ReportCard = styled(Card)`
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
  
  .report-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
  
  .report-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #141340;
    margin-bottom: 0.5rem;
  }
  
  .report-description {
    color: #6c757d;
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
  
  .empty-icon {
    font-size: 3rem;
    color: #dee2e6;
    margin-bottom: 1rem;
  }
  
  .empty-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
  }
  
  .empty-description {
    font-size: 0.9rem;
    color: #6c757d;
  }
`;

const CashierReports: React.FC = () => {
  const { user } = useAuth();
  const [assignedProject, setAssignedProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAssignedProject = async () => {
      try {
        setLoading(true);
        
        // Get projects assigned to the current cashier
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const assignedProjects = assignedProjectsResponse.data || [];
        
        if (assignedProjects.length > 0) {
          // For now, use the first assigned project
          // In the future, you might want to show multiple projects or let user select
          setAssignedProject(assignedProjects[0]);
        } else {
          setAssignedProject(null);
        }
      } catch (error) {
        console.error('Error loading assigned project:', error);
        setAssignedProject(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      loadAssignedProject();
    }
  }, [user]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!assignedProject) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>
            <div className="title-icon">
              <IconWrapper icon={FaChartBar} />
            </div>
            Reports
          </PageTitle>
        </PageHeader>
        
        <Card>
          <CardBody style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '3rem', color: '#dee2e6', marginBottom: '1rem' }}>
              <IconWrapper icon={FaExclamationTriangle} />
            </div>
            <h3 style={{ color: '#495057', marginBottom: '1rem' }}>No Project Assigned</h3>
            <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
              You are not currently assigned to any project. Please contact your supervisor to get assigned to a project.
            </p>
          </CardBody>
        </Card>
      </PageContainer>
    );
  }

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleGenerateReport = (reportType: string) => {
    // In a real application, this would generate and download the report
    alert(`${reportType} report is being generated for ${assignedProject.name}`);
  };

  const reports = [
    {
      id: 'expense-summary',
      title: 'Expense Summary Report',
      description: 'Comprehensive overview of all expenses recorded for this project, including totals by category and status.',
      icon: FaReceipt,
      action: () => handleGenerateReport('Expense Summary')
    },
    {
      id: 'budget-vs-actual',
      title: 'Budget vs Actual Report',
      description: 'Detailed comparison between budgeted amounts and actual expenses across all categories.',
      icon: FaMoneyBillWave,
      action: () => handleGenerateReport('Budget vs Actual')
    },
    {
      id: 'monthly-expenses',
      title: 'Monthly Expenses Report',
      description: 'Monthly breakdown of expenses with trends and analysis for better financial planning.',
      icon: FaCalendarAlt,
      action: () => handleGenerateReport('Monthly Expenses')
    },
    {
      id: 'supplier-analysis',
      title: 'Supplier Analysis Report',
      description: 'Analysis of expenses by supplier, including payment history and performance metrics.',
      icon: FaChartBar,
      action: () => handleGenerateReport('Supplier Analysis')
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <div className="title-icon">
            <IconWrapper icon={FaChartBar} />
          </div>
          Reports
        </PageTitle>
        <FlexContainer gap="1rem">
          <ActionButton>
            <IconWrapper icon={FaDownload} />
            Download All Reports
          </ActionButton>
        </FlexContainer>
      </PageHeader>

      {assignedProject ? (
        <ProjectInfoCard>
          <div className="project-header">
            <h3>{assignedProject.name}</h3>
            <div className="project-meta">
              Project ID: {assignedProject._id || assignedProject.id} • Budget: {formatCurrency(assignedProject.budget.total)}
            </div>
          </div>
          <CardBody>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>Location:</strong> {assignedProject.location}
              </div>
              <div>
                <strong>Project Manager:</strong> {
                  assignedProject.assignedStaff?.find((staff: any) => staff.role === 'Project Manager')?.name || 'Not assigned'
                }
              </div>
            </div>
          </CardBody>
        </ProjectInfoCard>
      ) : (
        <Card>
          <CardBody style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No project assigned. Please contact your supervisor to get assigned to a project.</p>
          </CardBody>
        </Card>
      )}

      <ReportsGrid>
        {reports.map((report) => (
          <ReportCard key={report.id} onClick={report.action}>
            <CardBody>
              <div className="report-icon">
                <IconWrapper icon={report.icon} />
              </div>
              <div className="report-title">{report.title}</div>
              <div className="report-description">{report.description}</div>
              <FlexContainer gap="0.5rem">
                <OutlineButton>
                  <IconWrapper icon={FaFilePdf} />
                  PDF
                </OutlineButton>
                <OutlineButton>
                  <IconWrapper icon={FaFileExcel} />
                  Excel
                </OutlineButton>
              </FlexContainer>
            </CardBody>
          </ReportCard>
        ))}
      </ReportsGrid>

      <Card>
        <CardBody>
          <h3 style={{ marginBottom: '1rem', color: '#141340' }}>Report Information</h3>
          <div style={{ lineHeight: '1.6', color: '#6c757d' }}>
            <p>
              <strong>Available Reports:</strong> All reports are generated based on the expenses recorded for your assigned project.
              Reports include real-time data and can be exported in PDF or Excel format.
            </p>
            <p>
              <strong>Report Schedule:</strong> Reports are generated on-demand. For regular reports, please contact your supervisor.
            </p>
            <p>
              <strong>Data Privacy:</strong> All financial data is kept confidential and is only accessible to authorized personnel.
            </p>
          </div>
        </CardBody>
      </Card>
      
      <div style={{ 
        textAlign: 'right', 
        padding: '2rem 1rem 1rem', 
        color: '#6c757d', 
        fontSize: '0.8rem' 
      }}>
        Developed by Sandfish
      </div>
    </PageContainer>
  );
};

export default CashierReports; 